import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button, Card } from "../components/ui";
import { api, type BatchOrderRow, type OrderBatch, type OrderBatchStatus } from "../data/api";
import { BatchOrdersClusterMap, type ClusterPoint } from "../features/programacion/BatchOrdersClusterMap";

// ProgramacionBatchDetailPage — vista de exploración del lote. Inspirada
// en "Detalles de la importación" de Drivin:
//   - Banner con resumen (estado, totales, geolocalizadas, sin coords)
//   - Búsqueda libre sobre la tabla (cliente / cod / dirección / código)
//   - Tabla a la izquierda + mapa con clusters a la derecha
//
// El mapa cluster-iza por zoom (Leaflet.markercluster). Al alejarse se
// agrupan en círculos con la cuenta; al acercarse se rompen en pines
// individuales hasta nivel de cuadra.

const STATUS_VARIANTS: Record<OrderBatchStatus, { variant: Parameters<typeof Badge>[0]["variant"]; label: string }> = {
  created:  { variant: "pending",   label: "Por planificar" },
  planning: { variant: "in-route",  label: "Planificando" },
  planned:  { variant: "delivered", label: "Planificado" },
};

export function ProgramacionBatchDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [batch, setBatch] = useState<OrderBatch | null>(null);
  const [orders, setOrders] = useState<BatchOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Pedimos el batch buscándolo en la lista (más simple que un
      // GET dedicado y reusa el mismo endpoint cacheable).
      const list = await api.listOrderBatches({ limit: 200 });
      const found = list.find((b) => b.id === id) ?? null;
      setBatch(found);
      const rows = await api.listOrdersByBatchIds([id]);
      setOrders(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // KPIs derivados de las órdenes ya hidratadas.
  const stats = useMemo(() => {
    const total = orders.length;
    let geo = 0;
    for (const o of orders) {
      if (o.lat != null && o.lon != null && Number.isFinite(o.lat) && Number.isFinite(o.lon)) {
        geo++;
      }
    }
    return { total, geo, noGeo: total - geo };
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      o.cliente.toLowerCase().includes(q) ||
      o.cod_cliente.toLowerCase().includes(q) ||
      o.order_code.toLowerCase().includes(q) ||
      o.transportista.toLowerCase().includes(q)
    );
  }, [orders, search]);

  // Solo los puntos geo-localizados van al mapa; la tabla muestra todos
  // (incluidos los sin coords, para que el operador los pueda corregir).
  const mapPoints = useMemo<ClusterPoint[]>(() => {
    const pts: ClusterPoint[] = [];
    for (const o of filtered) {
      if (o.lat == null || o.lon == null) continue;
      if (!Number.isFinite(o.lat) || !Number.isFinite(o.lon)) continue;
      pts.push({
        id: o.id,
        lat: o.lat,
        lon: o.lon,
        label: `${o.cliente} (${o.cod_cliente})`,
      });
    }
    return pts;
  }, [filtered]);

  const statusMeta = batch ? STATUS_VARIANTS[batch.status] : null;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* Header / banner */}
      <header className="px-4 pt-4 pb-3 border-b border-border bg-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/programacion" className="text-sub hover:text-ink text-lg leading-none" aria-label="Volver">
              ←
            </Link>
            <h1 className="text-xl font-semibold text-ink">
              Detalles del lote{batch ? `: ${batch.name}` : ""}
            </h1>
          </div>
          {batch && statusMeta && <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          {batch && (
            <span className="text-sub">
              <span className="text-ink font-medium">{batch.name}</span> · {formatDate(batch.created_at)}
            </span>
          )}
          <span>
            <strong className="text-ink">{stats.total}</strong>{" "}
            <span className="text-sub">Órdenes totales</span>
          </span>
          <span>
            <strong className="text-state-delivered">{stats.geo}</strong>{" "}
            <span className="text-sub">geolocalizadas</span>
          </span>
          <span>
            <strong className={stats.noGeo > 0 ? "text-state-failed" : "text-ink"}>{stats.noGeo}</strong>{" "}
            <span className="text-sub">sin coordenadas</span>
          </span>
        </div>
      </header>

      {error && (
        <div className="px-4 py-2 text-sm text-state-failed bg-state-failed/10 border-b border-state-failed/30">
          {error}
        </div>
      )}

      {/* Body: tabla izquierda, mapa derecha */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-3 p-3 min-h-0">
        <Card padding="none" className="flex flex-col min-h-0">
          <div className="p-3 border-b border-border">
            <input
              type="text"
              className="w-full border border-border rounded px-3 py-2"
              placeholder="Búsqueda por cliente, código o transportista…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-sub sticky top-0 bg-card border-b border-border">
                <tr>
                  <th className="px-3 py-2">Código</th>
                  <th className="px-3 py-2">Cliente</th>
                  <th className="px-3 py-2 text-center">Geo</th>
                  <th className="px-3 py-2">Transportista</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sub">
                      {orders.length === 0 ? "Este lote no tiene órdenes." : "Ningún resultado para la búsqueda."}
                    </td>
                  </tr>
                )}
                {filtered.map((o) => {
                  const isGeo = o.lat != null && o.lon != null && Number.isFinite(o.lat) && Number.isFinite(o.lon);
                  return (
                    <tr key={o.id} className="border-t border-border hover:bg-page">
                      <td className="px-3 py-2 font-mono text-xs text-sub">{o.cod_cliente}</td>
                      <td className="px-3 py-2 text-ink">{o.cliente}</td>
                      <td className="px-3 py-2 text-center">
                        {isGeo ? (
                          <span className="text-state-delivered" title="Geolocalizada">●</span>
                        ) : (
                          <span className="text-state-failed" title="Sin coordenadas">○</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sub">{o.transportista || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {o.valor_venta.toLocaleString("es-PE", { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card padding="none" className="overflow-hidden min-h-[400px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sub">Cargando mapa…</div>
          ) : (
            <BatchOrdersClusterMap points={mapPoints} />
          )}
        </Card>
      </div>

      {batch && batch.status !== "planned" && (
        <footer className="px-4 py-3 border-t border-border bg-card flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => void reload()} disabled={loading}>
            Recargar
          </Button>
          <Link to="/programacion">
            <Button variant="primary">Volver a la lista</Button>
          </Link>
        </footer>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
